import { InputLabel, useToast } from '@pm/ui';
import { Blob } from '@pm/upload';
import clsx from 'clsx';
import { FunctionComponent, useEffect } from 'react';
import { useDropzone } from 'react-dropzone-esm';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEnvironment } from '../../hooks/env';
import { Uploader } from './Uploader';

// TODO: Move this to a shared types file
export interface UploadAttachmentType extends File {
  blob?: Blob;
  key?: string;
}

type AttachmentUploaderProps =
  | {
      name: string;
      label?: string;
      placeholder?: string;
      deleteButtonLabel: string;
      deleteFlagName?: string;
      existingAttachment: string | null;
      setExistingAttachment: (existingAttachment: string | null) => void;
    }
  | {
      name: string;
      label?: string;
      placeholder?: string;
      deleteButtonLabel: string;
      deleteFlagName?: never;
      existingAttachment?: never;
      setExistingAttachment?: never;
    };

export const SingleAttachmentUploader: FunctionComponent<
  AttachmentUploaderProps
> = ({
  name,
  existingAttachment,
  setExistingAttachment,
  label,
  deleteButtonLabel,
  deleteFlagName,
  placeholder,
}) => {
  const { getFieldState, formState, setValue, watch, trigger } =
    useFormContext();
  const { negative } = useToast();
  const attachment = watch(name);
  const attachmentName = existingAttachment || attachment?.name;
  const { error } = getFieldState(name, formState);
  const { directUploadsUrl } = useEnvironment();
  const { t } = useTranslation('insurance-page');
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    onDrop: (acceptedFiles: UploadAttachmentType[], fileRejections) => {
      const acceptedFile = acceptedFiles[0];
      if (fileRejections.length) {
        const fileErrorMessages = fileRejections.map((fileRejection) =>
          fileRejection.errors
            .map((error) => {
              if (error.code === 'file-invalid-type') {
                return t('FileTypeErrorToast');
              }
              const fileMessage =
                error.code === 'file-too-large'
                  ? t('FileMaxSizeErrorToast')
                  : error.message;
              return fileMessage;
            })
            .join(' '),
        );
        return fileErrorMessages.map((fileErrorMessage) => {
          negative({ content: fileErrorMessage });
        });
      }
      if (acceptedFile == null) {
        negative({ content: t('FileEmptyErrorToast') });
        return;
      }
      acceptedFile.key = `${acceptedFile.name}-${Date.now()}`;
      setValue(name, acceptedFile);
      if (deleteFlagName) setValue(deleteFlagName, false);
    },
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB to bytes
  });

  useEffect(() => {
    if (!attachment) return;

    const uploader = new Uploader(attachment, directUploadsUrl);
    uploader.upload((blob) => {
      attachment.blob = blob;
    });
    trigger(name);
  }, [attachment, directUploadsUrl, name, trigger]);

  return (
    <div className="flex flex-col gap-s">
      {label && <InputLabel htmlFor={name}>{label}</InputLabel>}

      {existingAttachment || attachment ? (
        <div className="border-grey-200 flex justify-between rounded-lg border p-4">
          {attachmentName && (
            <span className="overflow-hidden text-ellipsis">
              {attachmentName}
            </span>
          )}
          <button
            type="button"
            data-testid="deleteButton"
            className="text-hyperlink-foreground text-base underline"
            onClick={() => {
              if (existingAttachment) setExistingAttachment(null);
              if (deleteFlagName) setValue(deleteFlagName, true);
              setValue(name, null, { shouldValidate: true });
            }}
          >
            {deleteButtonLabel}
          </button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={clsx(
            'border-grey-400 bg-grey-50 flex flex-col justify-center rounded-lg border border-dashed p-3.5 text-center text-sm',
            isDragActive && 'border-green-500 text-green-500',
            error && 'border-critical-dark',
          )}
        >
          <div className="text-grey-700 text-sm font-medium">{placeholder}</div>
          <input {...getInputProps()} id={name} data-testid="dropzone" />
        </div>
      )}
      {error && <p className="text-negative text-label">{error.message}</p>}
    </div>
  );
};
